import React, { useEffect, useState } from "react";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import LoaderSpinner from "../common/Loader";
import {
    fetchConfigurationDefaults,
    updateConfigurationDefaults,
    getWeightUnitList,
    getDimensionUnitList,
} from "../../store/actions";
import { GetAllLedgerCurrency } from "../../store/actions/accounts.action";
import { checkPermission } from "../../config/utils";
import {
    CMSEnum,
    featureSubmodule,
    InchEnum,
    settings,
    SUBF_DEFAULTSETTINGS,
    WEIGHT_UNIT_GM,
    WEIGHT_UNIT_KG,
    WEIGHT_UNIT_LB,
} from "../../constant";

// Windows TimeZoneInfo IDs accepted by backend TimeZoneInfo.FindSystemTimeZoneById.
const TIMEZONE_OPTIONS = [
    { id: 'UTC', label: '(UTC) Coordinated Universal Time' },
    { id: 'Hawaiian Standard Time', label: '(UTC-10:00) Hawaii' },
    { id: 'Alaskan Standard Time', label: '(UTC-09:00) Alaska' },
    { id: 'Pacific Standard Time', label: '(UTC-08:00) Pacific Time' },
    { id: 'Mountain Standard Time', label: '(UTC-07:00) Mountain Time' },
    { id: 'Central Standard Time', label: '(UTC-06:00) Central Time' },
    { id: 'Eastern Standard Time', label: '(UTC-05:00) Eastern Time' },
    { id: 'Atlantic Standard Time', label: '(UTC-04:00) Atlantic Time' },
    { id: 'GMT Standard Time', label: '(UTC+00:00) London, Dublin' },
    { id: 'Central European Standard Time', label: '(UTC+01:00) Central Europe' },
    { id: 'India Standard Time', label: '(UTC+05:30) India Standard Time' },
    { id: 'Singapore Standard Time', label: '(UTC+08:00) Singapore' },
    { id: 'China Standard Time', label: '(UTC+08:00) China Standard Time' },
    { id: 'Tokyo Standard Time', label: '(UTC+09:00) Tokyo' },
    { id: 'AUS Eastern Standard Time', label: '(UTC+10:00) Sydney' },
];

const DATE_FORMAT_OPTIONS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];
const TIME_FORMAT_OPTIONS = ['12H', '24H'];

const REQUIRED_PAYLOAD_FIELDS = [
    'weightUnitId',
    'dimensionUnitId',
    'timeZoneId',
    'dateFormat',
    'timeFormat',
    'currencyCode',
];

const DEFAULT_FORM = {
    weightUnitId: '',
    dimensionUnitId: '',
    timeZoneId: 'Eastern Standard Time',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12H',
    currencyCode: 'USD',
};

const readEntity = (payload) => payload?.Entity ?? payload?.entity ?? null;

const extractStatus = (response) => response?.data?.Status ?? response?.data?.status;

const extractMessage = (response) =>
    response?.data?.Message?.AppStatusDescription
    ?? response?.data?.message
    ?? '';

export default function ConfigurationDefaults(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userDetails = useSelector((state) => state.auth.user);
    const employeePermissions = useSelector((state) => state?.sidebar?.employeePermissions);
    const permissionEmployees = props?.employeePermissions || employeePermissions;
    const { configurationDefaults, configurationDefaultsStatus } =
        useSelector((state) => state.DefaultConfigReducer);
    const { weightUnitList = [], dimensionUnitList = [] } = useSelector((state) => state.shipment || {});
    const { ledgerCurrencyList = [] } = useSelector((state) => state.accountingReducer || {});

    const hasAccess = userDetails?.Value?.isSuperAdmin
        && checkPermission(settings, SUBF_DEFAULTSETTINGS, '', featureSubmodule, permissionEmployees);

    const [form, setForm] = useState(DEFAULT_FORM);
    const [initialForm, setInitialForm] = useState(DEFAULT_FORM);
    const [editable, setEditable] = useState(false);
    const [isFormEdited, setIsFormEdited] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        if (userDetails?.Value && !hasAccess) {
            navigate("/setting");
        }
    }, [userDetails, hasAccess, navigate]);

    useEffect(() => {
        if (!hasAccess) return;
        dispatch(fetchConfigurationDefaults());
        if (!weightUnitList.length) dispatch(getWeightUnitList());
        if (!dimensionUnitList.length) dispatch(getDimensionUnitList());
        if (!ledgerCurrencyList.length) dispatch(GetAllLedgerCurrency());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAccess, dispatch]);

    useEffect(() => {
        setShowLoader(configurationDefaultsStatus === 'pending');
    }, [configurationDefaultsStatus]);

    useEffect(() => {
        const entity = readEntity(configurationDefaults);
        if (entity) {
            const seeded = {
                weightUnitId: entity.weightUnitId ?? entity.WeightUnitId ?? '',
                dimensionUnitId: entity.dimensionUnitId ?? entity.DimensionUnitId ?? '',
                timeZoneId: entity.timeZoneId ?? entity.TimeZoneId ?? 'Eastern Standard Time',
                dateFormat: entity.dateFormat ?? entity.DateFormat ?? 'MM/DD/YYYY',
                timeFormat: entity.timeFormat ?? entity.TimeFormat ?? '12H',
                currencyCode: entity.currencyCode ?? entity.CurrencyCode ?? 'USD',
            };
            setForm(seeded);
            setInitialForm(seeded);
            setIsFormEdited(false);
        }
    }, [configurationDefaults]);

    const updateForm = (name, value) => {
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            if (name === 'weightUnitId' && value !== '') {
                const w = Number(value);
                next.dimensionUnitId = (w === WEIGHT_UNIT_KG || w === WEIGHT_UNIT_GM) ? CMSEnum : InchEnum;
            } else if (name === 'dimensionUnitId' && value !== '') {
                const d = Number(value);
                next.weightUnitId = d === InchEnum ? WEIGHT_UNIT_LB : WEIGHT_UNIT_KG;
            }
            const changed = Object.keys(next).some((k) => String(next[k]) !== String(initialForm[k]));
            setIsFormEdited(changed);
            return next;
        });
    };

    const handleSave = async () => {
        const payload = {
            weightUnitId: Number(form.weightUnitId) || 0,
            dimensionUnitId: Number(form.dimensionUnitId) || 0,
            timeZoneId: form.timeZoneId,
            dateFormat: form.dateFormat,
            timeFormat: form.timeFormat,
            currencyCode: form.currencyCode,
        };
        if (REQUIRED_PAYLOAD_FIELDS.some((f) => !payload[f])) {
            toast.error('All fields are required');
            return;
        }
        setShowLoader(true);
        try {
            const response = await Promise.resolve(dispatch(updateConfigurationDefaults(payload)));
            const status = extractStatus(response);
            if (status === 200) {
                toast.success('Default configuration settings saved successfully');
                setEditable(false);
                setIsFormEdited(false);
                dispatch(fetchConfigurationDefaults());
            } else {
                toast.error(extractMessage(response) || 'Failed to save default configuration settings');
            }
        } catch {
            toast.error('An error occurred while saving default configuration settings');
        }
        setShowLoader(false);
    };

    const handleCancel = () => {
        setForm(initialForm);
        setIsFormEdited(false);
        setEditable(false);
    };

    if (!hasAccess) return null;

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    Configuration
                    <div className="top_right_button_container">
                        <Button
                            className="cus-primary-transparent-btn"
                            onClick={() => navigate("/default-settings")}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                <div className="container-fluid">
                    <div className="cus-page-content-sec">
                        <div className="d-flex justify-content-between align-items-center pl-3 pr-3 pt-3">
                            <h5 className="mb-0">Default Units, Time Zone &amp; Date Format</h5>
                            <div className="top_right_button_container">
                                {editable && (
                                    <button
                                        type="button"
                                        className="btn cus-primary-transparent-btn top-btn"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn cus-seconday-bg-btn top-btn editBtn"
                                    onClick={() => setEditable(true)}
                                    disabled={editable}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={handleSave}
                                    disabled={!editable || !isFormEdited}
                                >
                                    Save
                                </button>
                            </div>
                        </div>

                        <div className="mb-3 pl-3 pr-3 mt-3">
                            <div className="alert alert-info" role="alert" style={{ fontSize: '13px' }}>
                                <i className="fa fa-info-circle me-2 pr-1" aria-hidden="true"></i>These settings apply only to newly created seller accounts. Existing seller configurations are not affected.
                            </div>
                        </div>

                        {showLoader && <LoaderSpinner />}

                        <div className="pl-3 pr-3 pb-3">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="weightUnitId"><strong>Weight unit</strong></label>
                                    <select
                                        id="weightUnitId"
                                        className="form-control"
                                        value={form.weightUnitId}
                                        onChange={(e) => updateForm('weightUnitId', e.target.value)}
                                        disabled={!editable}
                                    >
                                        <option value="">Select</option>
                                        {weightUnitList.map((opt) => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="dimensionUnitId"><strong>Dimension unit</strong></label>
                                    <select
                                        id="dimensionUnitId"
                                        className="form-control"
                                        value={form.dimensionUnitId}
                                        onChange={(e) => updateForm('dimensionUnitId', e.target.value)}
                                        disabled={!editable}
                                    >
                                        <option value="">Select</option>
                                        {dimensionUnitList.map((opt) => (
                                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="timeZoneId"><strong>Time zone</strong></label>
                                    <select
                                        id="timeZoneId"
                                        className="form-control"
                                        value={form.timeZoneId}
                                        onChange={(e) => updateForm('timeZoneId', e.target.value)}
                                        disabled={!editable}
                                    >
                                        {TIMEZONE_OPTIONS.map((tz) => (
                                            <option key={tz.id} value={tz.id}>{tz.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="dateFormat"><strong>Date format</strong></label>
                                    <select
                                        id="dateFormat"
                                        className="form-control"
                                        value={form.dateFormat}
                                        onChange={(e) => updateForm('dateFormat', e.target.value)}
                                        disabled={!editable}
                                    >
                                        {DATE_FORMAT_OPTIONS.map((f) => (
                                            <option key={f} value={f}>{f}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="timeFormat"><strong>Time format</strong></label>
                                    <select
                                        id="timeFormat"
                                        className="form-control"
                                        value={form.timeFormat}
                                        onChange={(e) => updateForm('timeFormat', e.target.value)}
                                        disabled={!editable}
                                    >
                                        {TIME_FORMAT_OPTIONS.map((f) => (
                                            <option key={f} value={f}>{f === '12H' ? '12 hour' : '24 hour'}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="currencyCode"><strong>Currency</strong></label>
                                    <select
                                        id="currencyCode"
                                        className="form-control"
                                        value={form.currencyCode}
                                        onChange={(e) => updateForm('currencyCode', e.target.value)}
                                        disabled={!editable}
                                    >
                                        {ledgerCurrencyList.length === 0 && (
                                            <option value="USD">USD</option>
                                        )}
                                        {ledgerCurrencyList.map((opt) => {
                                            const code = opt?.currencyCode ?? opt?.currencyName ?? opt?.code;
                                            return <option key={opt.id ?? code} value={code}>{opt.currencyName ?? code}</option>;
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

ConfigurationDefaults.propTypes = {
    employeePermissions: PropTypes.array,
};
